import React, { useState, useEffect } from "react";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import Composer from "./Composer";

export default function ChatWindow({ selectedPhone, messages, socket, onBack }) {
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  // In-chat search state
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchMessageIndexes, setMatchMessageIndexes] = useState([]);
  const [currentMatchPointer, setCurrentMatchPointer] = useState(0);

  // Filter messages for selected phone
  useEffect(() => {
    if (selectedPhone) {
      setLoadingMessages(true);
      setTimeout(() => {
        const filtered = messages.filter((m) => m.phone_number === selectedPhone);
        setChatMessages(filtered.reverse());
        setLoadingMessages(false);
      }, 300);
    }
    // reset search when switching conversation
    setSearchQuery("");
    setSearchVisible(false);
    setMatchMessageIndexes([]);
    setCurrentMatchPointer(0);
  }, [selectedPhone, messages]);

  // Listen for new messages via socket
  useEffect(() => {
    const handleNewMessage = (msg) => {
      if (msg.phone_number === selectedPhone) {
        setChatMessages((prev) => {
          if (prev.some((m) => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
      }
    };

    const handleStatusUpdate = (updatedMsg) => {
      setChatMessages((prev) =>
        prev.map((m) =>
          m._id === updatedMsg._id ? { ...m, status: updatedMsg.status } : m
        )
      );
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("messageStatusUpdate", handleStatusUpdate);

    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("messageStatusUpdate", handleStatusUpdate);
    };
  }, [selectedPhone, socket]);

  // Empty state - only show on desktop
  if (!selectedPhone) {
    return (
      <div
        className="hidden sm:flex flex-1 items-center justify-center text-gray-400"
        style={{ backgroundColor: "#FBF8F2" }}
      >
        <div className="text-center">
          <svg
            className="w-32 h-32 mx-auto mb-6 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">
            Select a conversation
          </h3>
          <p className="text-sm text-gray-400">
            Choose a contact to start messaging
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-1 flex-col w-full h-screen"
      style={{ backgroundColor: "#FBF8F2" }}
    >
      {/* Mobile Back Button */}
      <div className="sm:hidden flex items-center gap-3 p-4 border-b border-gray-200 bg-white">
        <button
          onClick={onBack}
          className="text-blue-500 font-medium text-lg"
        >
          ← Back
        </button>
      </div>

      <ChatHeader
        selectedPhone={selectedPhone}
        searchVisible={searchVisible}
        toggleSearch={() => setSearchVisible((s) => !s)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onNext={() => {
          if (matchMessageIndexes.length === 0) return;
          setCurrentMatchPointer((p) => (p + 1) % matchMessageIndexes.length);
        }}
        onPrev={() => {
          if (matchMessageIndexes.length === 0) return;
          setCurrentMatchPointer((p) => (p - 1 + matchMessageIndexes.length) % matchMessageIndexes.length);
        }}
        matchCount={matchMessageIndexes.length}
        currentMatchIndex={matchMessageIndexes.length ? currentMatchPointer + 1 : 0}
      />

      <MessageList
        messages={chatMessages}
        loading={loadingMessages}
        highlightText={searchQuery}
        activeMessageIndex={matchMessageIndexes.length ? matchMessageIndexes[currentMatchPointer] : -1}
        onComputeMatches={(indexes) => {
          setMatchMessageIndexes(indexes);
          if (indexes.length === 0) {
            setCurrentMatchPointer(0);
          } else if (currentMatchPointer >= indexes.length) {
            setCurrentMatchPointer(0);
          }
        }}
      />

      <Composer selectedPhone={selectedPhone} />
    </div>
  );
}